import { useEffect, useState, type FormEvent } from 'react'
import { Helmet } from 'react-helmet-async'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Checkbox } from '../../components/atoms/Checkbox'
import { Input } from '../../components/atoms/Input'
import { useAccountProfile } from '../../../viewmodels/account/useAccountProfile'

/** `/account/profile` — view + edit name, phone, marketing prefs. */
export function AccountProfilePage(): JSX.Element {
  const { user, update } = useAccountProfile()
  const [firstName, setFirstName] = useState(user?.firstName ?? '')
  const [lastName, setLastName] = useState(user?.lastName ?? '')
  const [phone, setPhone] = useState(user?.phone ?? '')
  const [marketing, setMarketing] = useState(user?.acceptsMarketing ?? false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<string | null>(null)

  // Re-sync if AuthContext refreshes the user underneath us.
  useEffect(() => {
    setFirstName(user?.firstName ?? '')
    setLastName(user?.lastName ?? '')
    setPhone(user?.phone ?? '')
    setMarketing(user?.acceptsMarketing ?? false)
  }, [user])

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    setError(null)
    setSavedAt(null)
    setSaving(true)
    const result = await update({
      firstName: firstName.trim() || null,
      lastName: lastName.trim() || null,
      phone: phone.trim() || null,
      acceptsMarketing: marketing
    })
    if (result.ok) {
      setSavedAt(new Date().toISOString())
    } else {
      setError(result.message)
    }
    setSaving(false)
  }

  return (
    <>
      <Helmet>
        <title>Profile — Uyyamadilu</title>
      </Helmet>
      <h1 className="font-heading text-h1 text-charcoal">Profile</h1>
      <p className="mt-2 font-body text-body text-text-secondary mb-6">
        Email <span className="font-mono text-body-sm">{user?.email ?? ''}</span> is the
        unique identifier for your account and cannot be changed here.
      </p>

      {error !== null ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}
      {savedAt !== null ? (
        <div className="mb-4">
          <Alert tone="success">Saved.</Alert>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-4 max-w-xl" noValidate>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="font-body text-body-sm text-text-secondary block mb-1">First name</span>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </label>
          <label className="block">
            <span className="font-body text-body-sm text-text-secondary block mb-1">Last name</span>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </label>
        </div>
        <label className="block">
          <span className="font-body text-body-sm text-text-secondary block mb-1">Phone</span>
          <Input
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </label>
        <Checkbox
          checked={marketing}
          onChange={(e) => setMarketing(e.target.checked)}
          label="Send me occasional updates about new products and offers."
        />
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </form>
    </>
  )
}
